import { FormEvent, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { Spinner } from "../components/Loaders";

export function Register() {
  const { firebaseUser, register, loginWithGoogle, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busyAction, setBusyAction] = useState<"email" | "google" | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (firebaseUser) return <Navigate to="/" replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setBusyAction("email");
    try {
      await register(email.trim(), password, displayName.trim() || undefined);
      await refreshProfile();
      navigate("/", { replace: true });
    } catch (err) {
      const msg = (err as Error).message || "Registration failed";
      setError(msg.replace("Firebase: ", ""));
    } finally {
      setBusyAction(null);
    }
  };

  const onGoogleSignUp = async () => {
    setError(null);
    setBusyAction("google");
    try {
      await loginWithGoogle();
      await refreshProfile();
      navigate("/", { replace: true });
    } catch (err) {
      const msg = (err as Error).message || "Google sign-in failed";
      setError(msg.replace("Firebase: ", ""));
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1>Create account</h1>
        <p className="sub">Free forever. 3 deck builds per day.</p>
        <form onSubmit={onSubmit}>
          {error && <div className="form-error">{error}</div>}
          <label>
            <span>Display name (optional)</span>
            <input
              type="text"
              autoComplete="name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </label>
          <label>
            <span>Email</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label>
            <span>Password (min 6 characters)</span>
            <input
              type="password"
              required
              minLength={6}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <button className="primary" type="submit" disabled={busyAction !== null}>
            {busyAction === "email" ? (
              <>
                <Spinner className="button-spinner" />
                Creating...
              </>
            ) : (
              "Create account"
            )}
          </button>
          <div className="auth-divider">or</div>
          <button
            className="secondary"
            type="button"
            onClick={onGoogleSignUp}
            disabled={busyAction !== null}
          >
            {busyAction === "google" ? (
              <>
                <Spinner className="button-spinner" />
                Connecting Google...
              </>
            ) : (
              "Sign up with Google"
            )}
          </button>
        </form>
        <p className="auth-foot">
          Already have one? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
